import { action, computed, thunk, thunkOn, createStore } from "easy-peasy";
import {compact, forEach, get, isEmpty, maxBy, merge} from "lodash";
import * as Url from "domurl";

import api from "../services/api";
import { DefaultActiveSeries } from "../components/Graph";

export const types = {
  CHANGE_SIMULATION_TYPE: "CHANGE_SIMULATION_TYPE",
  IS_FOCUSING_CURVE: "IS_FOCUSING_CURVE",

  LOAD_GRAPH: "LOAD_GRAPH",
  LOAD_ACOUSTIC: "LOAD_ACOUSTIC",
  LOAD_ERP: "LOAD_ERP",
  LOAD_FAN_INFO: "LOAD_FAN_INFO",
  LOAD_RESISTIVE: "LOAD_RESISTIVE",

  START_LOADING: "START_LOADING",
  STOP_LOADING: "STOP_LOADING",
  HAS_DESIGN_POINT: "HAS_DESIGN_POINT",
  HAS_SOUND_DATA: "HAS_SOUND_DATA",
  IS_LOADING: "IS_LOADING",
  FAN_PRICES: "FAN_PRICES",
  FAN_CODE: "FAN_CODE",
  FAN_AVAILABILITY: "FAN_AVAILABILITY",
  FAN_WEIGHT: "FAN_WEIGHT",

  SET_ACOUSTIC_DATA: "SET_ACOUSTIC_DATA",
  SET_ACTIVE_SERIES: "SET_ACTIVE_SERIES",
  SET_ACOUSTIC_CONFIG: "SET_ACOUSTIC_CONFIG",
  SET_CUSTOM_FREQUENCY: "SET_CUSTOM_FREQUENCY",
  SET_CUSTOM_VARIANT: "SET_CUSTOM_VARIANT",
  SET_ERP_HTML: "SET_ERP_HTML",
  SET_FOCUSED_PAIR: "SET_FOCUSED_PAIR",
  SET_DESIGN_POINT: "SET_DESIGN_POINT",
  SET_CUSTOM_POWER_LINE: "SET_CUSTOM_POWER_LINE",
  SET_ENDPOINT: "SET_ENDPOINT",
  SET_FAN: "SET_FAN",
  SET_GRAPH: "SET_GRAPH",
  SET_SIMULATION_TYPE: "SET_SIMULATION_TYPE",
  RELOAD_GRAPH: "RELOAD_GRAPH",
  TOGGLE_ALTERNATIVE_POINT: "TOGGLE_ALTERNATIVE_POINT",
  SHOW_OR_HIDE_SPARE_PARTS: "SHOW_OR_HIDE_SPARE_PARTS"
};

const graphNeedsReload = (graph, oldDesignPoint, designPoint) => {
  // we need this for the initial load, when the graph is empty
  const graphChanged = graph.temperature !== designPoint.temperature ||
    graph.height !== designPoint.height ||
    graph.frequency !== designPoint.frequency ||
    graph.variant !== designPoint.variant ||
    graph.density !== designPoint.density;

  const designPointChanged = oldDesignPoint.temperature !== designPoint.temperature ||
    oldDesignPoint.height !== designPoint.height ||
    oldDesignPoint.frequency !== designPoint.frequency ||
    oldDesignPoint.variant !== designPoint.variant ||
    oldDesignPoint.density_mode !== designPoint.density_mode ||
    oldDesignPoint.density !== designPoint.density ||
    oldDesignPoint.air_max_temperature !== designPoint.air_max_temperature;

  return graphChanged || designPointChanged;
};

const selection = {
  acousticData: {
    spectra: null,
    availableTypes: {},
    distance: "",
    type: "",
    directionality: "",
    minDistance: "",
    minDistanceMessageError: "",
  },
  activeSeries: DefaultActiveSeries,
  erpHTML: "",
  fan: {
    availableMotors: [],
    hasMotor: true,
    hasVariants: false,
    minPower: 0,
    maxPower: 0,
    multipleCurves: false,
    customizableType: {
      allow_custom_power_in_graph: false,
      weight_calculated: false,
    },
    motor: {
      power: 0
    },
    erp: {
      variant_id: null,
      curve_id: null,
      motor_id: null
    },
    prices: null,
    variants: null,
    documentationUrl: "",
  },
  features: [],
  history: null,
  loading: 0,
  endpoint: '',
  focusedPair: '',
  showAlternativePoint: false,
  designPoint: {
    flow: 0,
    pressure: 0,
    temperature: 20,
    height: 0,
    density_mode: "calculated", // "calculated" or "user_defined"
    density: 1.2, // Kg/m³
    air_max_temperature: 20, // °C
    frequency: null,
    variant: "", // Use an empty string to control select dom element with React
  },
  graph: {},
  servicePoint: {},
  customizations: {
    // We use empty strings so form inputs are controlled by React
    simulationType: "",
    powerLine: {
      motorId: null,
      power: "", // Use an empty string to control select dom element with React
    }
  },
  reportUrl: "",
  sections: [],
  temperatures: {
    min: undefined,
    max: 200
  },
  units: {},
  soundDirectionalityOptions: {},

  // actions
  [types.SET_CUSTOM_FREQUENCY]: thunk(async (actions, frequency, { getState }) => {
    const { designPoint } = getState();

    designPoint.frequency = frequency;

    actions[types.SET_DESIGN_POINT]({ ...designPoint });
  }),
  [types.SET_CUSTOM_POWER_LINE]: action((state, { motorId, power }) => {
    state.customizations.powerLine = { motorId: motorId, power: power };
  }),
  [types.SET_CUSTOM_VARIANT]: thunk(async (actions, variant, { getState }) => {
    const { designPoint } = getState();

    designPoint.variant = variant;

    actions[types.SET_DESIGN_POINT]({ ...designPoint });
  }),
  [types.SET_ACTIVE_SERIES]: action((state, activeSeries) => {
    state.activeSeries = activeSeries;
  }),
  [types.SET_ACOUSTIC_DATA]: action((state, data) => {
    state.acousticData = data;
  }),
  [types.SET_ACOUSTIC_CONFIG]: action((state, config) => {
    state.acousticData = { ...state.acousticData, ...config };
  }),
  [types.SET_ERP_HTML]: action((state, html) => {
    state.erpHTML = html;
  }),
  [types.SET_FOCUSED_PAIR]: action((state, focusedPair) => {
    state.focusedPair = focusedPair;
  }),
  [types.TOGGLE_ALTERNATIVE_POINT]: action((state) => {
    state.showAlternativePoint = !state.showAlternativePoint;

        // Update the browser url
    const { history } = state;

    const url = new Url();

    url.query.alternativePoint = state.showAlternativePoint ? "1" : "0";
    history.push({ search: String(url.query), state: {...state.designPoint, alternativePoint: state.showAlternativePoint} });
  }),
  [types.SET_SIMULATION_TYPE]: action((state, type) => {
    state.customizations.simulationType = type;
  }),
  [types.CHANGE_SIMULATION_TYPE]: thunk(async (actions, { type, reload }, { getState }) => {
    const { fan } = getState();

    actions[types.SET_SIMULATION_TYPE](type);

    if (reload !== false) {
      actions[types.SET_CUSTOM_FREQUENCY](fan.frequency);
    }
  }),
  [types.SET_DESIGN_POINT]: thunk(async (actions, designPoint, { getState }) => {
    const { fan, graph, units, designPoint: oldDesignPoint } = getState();

    const loadGraph = () => {
      if (graphNeedsReload(graph, oldDesignPoint, designPoint)) {
        // actions[types.START_LOADING]();

        try {
          return api.graph(fan.id, designPoint, units);
          // actions[types.STOP_LOADING]();
          // actions[types.SET_GRAPH](data);
          // actions[types.LOAD_RESISTIVE]();
        } catch (error) {
          // actions[types.STOP_LOADING]();
          // createFlash(errorMessages[errorMessagesTypes.FETCH_REPOS]);
        }
      } else {
        return new Promise((resolve, reject) => {
          // Llamamos a resolve(...) cuando lo que estabamos haciendo finaliza con éxito, y reject(...) cuando falla.
          // En este ejemplo, usamos setTimeout(...) para simular código asíncrono.
          // En la vida real, probablemente uses algo como XHR o una API HTML5.
          // setTimeout(function(){
            resolve({ data: graph }); // ¡Todo salió bien!
          // }, 100);
        });
      }
    };

    const loadResistive = () => {
      if (designPoint.flow > 0 && designPoint.pressure > 0) {
        // actions[types.START_LOADING]();

        try {
          return api.resistive(fan.id, designPoint, units);
        } catch (error) {
          // actions[types.STOP_LOADING]();
          // createFlash(errorMessages[errorMessagesTypes.FETCH_REPOS]);
        }
      } else {
        return new Promise((resolve, reject) => {
          // Llamamos a resolve(...) cuando lo que estabamos haciendo finaliza con éxito, y reject(...) cuando falla.
          // En este ejemplo, usamos setTimeout(...) para simular código asíncrono.
          // En la vida real, probablemente uses algo como XHR o una API HTML5.
          // setTimeout(function(){
          resolve({ data: { } }); // ¡Todo salió bien!
          // }, 100);
        });
      }
    };

    actions[types.START_LOADING]();
    const [graphData, servicePoint] = await Promise.all([
      loadGraph(),
      loadResistive(),
    ]);
    actions[types.STOP_LOADING]();

    actions[types.RELOAD_GRAPH]({ graphData: graphData.data, servicePoint: servicePoint.data, designPoint: designPoint });
  }),
  [types.SET_ENDPOINT]: action((state, endpoint) => {
    api.setBaseUrl(endpoint);

    state.endpoint = endpoint;
  }),
  [types.SET_FAN]: action((state, fan) => {
    state.fan = fan;
  }),
  [types.SET_GRAPH]: action((state, graph) => {
    state.graph = graph;
  }),
  [types.RELOAD_GRAPH]: action((state, { graphData, servicePoint, designPoint }) => {
    state.graph = { ...graphData };
    state.servicePoint = { ...servicePoint };
    state.designPoint = { ...designPoint };

    if (state.showAlternativePoint && isEmpty(state.servicePoint.alternative_service_points)) {
      state.showAlternativePoint = false;
    }

    // Update the browser url
    const { history } = state;
    const urlParams = get(servicePoint, "params", false);

    const url = new Url();

    if (state.fan.hasVariants && url.query.variant != designPoint.variant) {
      url.query.variant = designPoint.variant;
      url.query.alternativePoint = state.showAlternativePoint ? "1" : "0";
      history.push({ search: String(url.query), state: {...designPoint, alternativePoint: state.showAlternativePoint} });
    }

    if (urlParams) {
      url.clearQuery();
      forEach(urlParams || [], (value, key) => url.query[key] = value);
      url.query.alternativePoint = state.showAlternativePoint ? "1" : "0";
      history.push({ search: String(url.query), state: {...designPoint, alternativePoint: state.showAlternativePoint} });
    }
  }),
  [types.START_LOADING]: action((state) => {
    state.loading = state.loading++;
  }),
  [types.STOP_LOADING]: action((state) => {
    state.loading = state.loading--;
  }),

  // computed
  [types.HAS_SOUND_DATA]: computed(state => !isEmpty(state.acousticData.spectra)),
  [types.IS_LOADING]: computed(state => state.loading > 0),
  [types.FAN_PRICES]: computed(state => {
    const { fan, servicePoint: { service_points, alternative_service_points }, showAlternativePoint } = state;

    let prices = fan.prices;

    // const curves = showAlternativePoint ? compact(alternative_service_points || []) : compact(service_points || []);

    // if (curves.length > 0) {
    //   prices = curves[0].info.prices;

    //   curves.forEach((curve) => {
    //     if (curve.info.prices) {
    //       Object.keys(curve.info.prices).forEach((priceName) => {
    //         prices[priceName] = maxBy([prices[priceName] || {}, curve.info.prices[priceName]], (price) => (price.price || 0));
    //       })
    //     }
    //   });
    // }

    return prices;
  }),
  [types.FAN_CODE]: computed(state => {
    const { fan, servicePoint: { service_points, alternative_service_points }, showAlternativePoint } = state;
    // const curves = showAlternativePoint ? compact(alternative_service_points || []) : compact(service_points || []);

    // return curves.length > 0 ? curves[0].info.fanCode : fan.code;

    return fan.code;
  }),

  [types.FAN_AVAILABILITY]: computed(state => {
    const { fan } = state;

    return fan.availability;
  }),
  [types.FAN_WEIGHT]: computed(state => {
    const { fan } = state;

    return fan.weight > 0 ? fan.weight : null;
  }),
  [types.HAS_DESIGN_POINT]: computed(state => {
    const { designPoint: { flow, pressure } } = state;

    return flow !== 0 && pressure !== 0;
  }),
  [types.IS_FOCUSING_CURVE]: computed(state => {
    const { focusedPair } = state;

    return !state[types.HAS_DESIGN_POINT] && focusedPair !== '';
  }),

  // thunks
  // [types.LOAD_GRAPH]: thunkOn(
  //   actions => actions[types.SET_DESIGN_POINT],
  //   async (actions, { payload }, { getStoreState, dispatch, injections }) => {
  //   const { designPoint, fan, graph } = getStoreState().selection;
  //
  //   if(graph.temperature !== payload.temperature || graph.height !== payload.height) {
  //     actions[types.START_LOADING]();
  //
  //     try {
  //       const {data} = await api.graph(fan.id, designPoint);
  //       actions[types.STOP_LOADING]();
  //       actions[types.SET_GRAPH](data);
  //       actions[types.LOAD_RESISTIVE]();
  //     } catch (error) {
  //       actions[types.STOP_LOADING]();
  //       // createFlash(errorMessages[errorMessagesTypes.FETCH_REPOS]);
  //     }
  //   }
  // }),
  // [types.LOAD_RESISTIVE]: thunkOn(
  //   actions => actions[types.SET_DESIGN_POINT],
  //   async (actions, { payload }, { getStoreState, dispatch, injections }) => {
  //   const { fan, designPoint } = getStoreState().selection;
  //
  //   if (payload.flow > 0 && payload.pressure > 0) {
  //     actions[types.START_LOADING]();
  //
  //     try {
  //       const {data} = await api.resistive(fan.id, designPoint);
  //       actions[types.STOP_LOADING]();
  //     } catch (error) {
  //       actions[types.STOP_LOADING]();
  //       // createFlash(errorMessages[errorMessagesTypes.FETCH_REPOS]);
  //     }
  //   }
  // }),
  [types.LOAD_ACOUSTIC]: thunkOn(
    actions => [
      actions[types.RELOAD_GRAPH],
      actions[types.TOGGLE_ALTERNATIVE_POINT],
      actions[types.SET_ACOUSTIC_CONFIG],
    ],
    async (actions, { payload }, { getStoreState, dispatch, injections }) => {
      const { acousticData, fan, servicePoint: { service_points, alternative_service_points }, showAlternativePoint, units } = getStoreState().selection;

      const curves = showAlternativePoint ? compact(alternative_service_points || []) : compact(service_points || []);

      if (curves.length > 0) {
        const points = curves.map((curve) => ({ curve_id: curve.curve_id, flow: curve.flow, frequency: curve.frequency }));

        actions[types.START_LOADING]();

        try {
          const {data} = await api.acoustic(fan.id, points, acousticData.distance, acousticData.type, acousticData.directionality, units);
          actions[types.STOP_LOADING]();
          actions[types.SET_ACOUSTIC_DATA](data);
          actions[types.LOAD_RESISTIVE]();
        } catch (error) {
          actions[types.STOP_LOADING]();
          // createFlash(errorMessages[errorMessagesTypes.FETCH_REPOS]);
        }
      } else {
        actions[types.SET_ACOUSTIC_DATA]({});
      }
    }
  ),
  [types.LOAD_ERP]: thunkOn(
    actions => [
      actions[types.RELOAD_GRAPH],
      actions[types.TOGGLE_ALTERNATIVE_POINT],
      actions[types.SET_FOCUSED_PAIR]
    ],
    async (actions, { payload }, { getStoreState, dispatch, injections }) => {
      const {
        fan,
        designPoint,
        graph: { curves: graphCurves },
        servicePoint: { service_points, alternative_service_points },
        showAlternativePoint,
        focusedPair,
        [types.IS_FOCUSING_CURVE]: isFocusingCurve
      } = getStoreState().selection;

      const defaultData = { variant_id: null, curve_id: null, motor_id: null };
      const curves = showAlternativePoint ? compact(alternative_service_points || [defaultData]) : compact(service_points || [defaultData]);

      let variantId = designPoint.variant;
      let curveId = curves[0].curve_id;
      let motorId = curves[0].motor_id;

      if (curveId == null && isFocusingCurve) {
        curveId = graphCurves.find(curve => curve.pair_identifier === focusedPair).id;
      }

      if (fan.speeds === 1 && fan.hasMotor === true) {
        curveId = fan.erp.curve_id;
        motorId = fan.erp.motor_id;
      }

      if (curveId !== null) {
        actions[types.START_LOADING]();

        try {
          const {data} = await api.erp(fan.id, variantId, curveId, motorId);
          actions[types.STOP_LOADING]();
          actions[types.SET_ERP_HTML](data);
        } catch (error) {
          actions[types.STOP_LOADING]();
          // createFlash(errorMessages[errorMessagesTypes.FETCH_REPOS]);
        }
      } else {
        actions[types.SET_ERP_HTML]("");
      }
    }
  ),
  [types.LOAD_FAN_INFO]: thunkOn(
    actions => [
      actions[types.RELOAD_GRAPH],
      actions[types.TOGGLE_ALTERNATIVE_POINT],
      actions[types.SET_FOCUSED_PAIR]
    ],
    async (actions, { payload }, { getStoreState, dispatch, injections }) => {
      const {
        fan,
        designPoint,
        graph: { curves: graphCurves },
        servicePoint: { service_points, alternative_service_points, metadata },
        showAlternativePoint,
        focusedPair,
        [types.IS_FOCUSING_CURVE]: isFocusingCurve
      } = getStoreState().selection;

      let motor = {};
      if (metadata) {
        motor = (showAlternativePoint ? metadata['alternative_service_points']['motor'] : metadata['service_points']['motor']) || {};
      }

      const defaultData = { variant_id: null, curve_id: null, motor_id: null };
      const curves = showAlternativePoint ? compact(alternative_service_points || [defaultData]) : compact(service_points || [defaultData]);

      let variantId = designPoint.variant;
      let curveId = curves[0].curve_id;

      if (curveId == null && isFocusingCurve) {
        curveId = curveId = graphCurves.find(curve => curve.pair_identifier === focusedPair).id;;
      }

      actions[types.START_LOADING]();

      try {
        const {data} = await api.fanInfo(
          fan.id,
          ["code", "name", "prices", "weight", "availableMotors", "availability", "documentationUrl"],
          { variant_id: variantId, curve_id: curveId, motor_id: motor.id }
        );

        actions[types.STOP_LOADING]();
        actions[types.SET_FAN]({ ...fan, ...data });
      } catch (error) {
        actions[types.STOP_LOADING]();
      }
    }
  ),
  [types.SHOW_OR_HIDE_SPARE_PARTS]: thunkOn(
    actions => [
      actions[types.RELOAD_GRAPH],
      actions[types.TOGGLE_ALTERNATIVE_POINT],
    ],
    async (actions, { payload }, { getStoreState, dispatch, injections }) => {
      const { servicePoint: { service_points, alternative_service_points }, showAlternativePoint } = getStoreState().selection;

      const defaultData = { rpm_angle: { rpm: null, angle: null } };
      const curves = showAlternativePoint ? compact(alternative_service_points || [defaultData]) : compact(service_points || [defaultData]);

      if (window.fwApp.SpareParts) {
        window.fwApp.SpareParts.toggle(curves[0].rpm_angle.rpm, curves[0].rpm_angle.angle);
      }
    }
  ),
};

export function configureStore(initialState = {}) {
  return createStore(
    {
      selection: merge(selection, initialState)
    },
    // An optional object for configuration
    {
      // middleware: [logger],
      injections: {
        // api,
      },
    }
  );
}
